import type { ReactNode } from "react";
import { cn } from "../../utils/cn";

export type BadgeVariant =
  | "active"
  | "inactive"
  | "pending"
  | "overdue"
  | "paid"
  | "partial"
  | "completed"
  | "cancelled";

export type BadgeColor = "success" | "warning" | "danger" | "info" | "secondary" | "primary" | "accent";

const VARIANT_TO_COLOR: Record<BadgeVariant, BadgeColor> = {
  active: "success",
  inactive: "secondary",
  pending: "warning",
  overdue: "danger",
  paid: "success",
  partial: "warning",
  completed: "success",
  cancelled: "danger",
};

const COLOR_STYLES: Record<BadgeColor, string> = {
  success: "bg-success-100 text-success-800",
  warning: "bg-warning-100 text-warning-800",
  danger: "bg-danger-100 text-danger-700",
  info: "bg-info-100 text-info-800",
  secondary: "bg-secondary-100 text-secondary-800",
  primary: "bg-primary-100 text-primary-800",
  accent: "bg-accent-100 text-accent-800",
};

const DOT_STYLES: Record<BadgeColor, string> = {
  success: "bg-success-500",
  warning: "bg-warning-500",
  danger: "bg-danger-500",
  info: "bg-info-500",
  secondary: "bg-secondary-500",
  primary: "bg-primary-500",
  accent: "bg-accent-500",
};

interface BadgeProps {
  variant?: BadgeVariant;
  color?: BadgeColor;
  children: ReactNode;
  dot?: boolean;
  className?: string;
}

export function Badge({ variant, color, children, dot, className }: BadgeProps) {
  const resolvedColor = color ?? (variant ? VARIANT_TO_COLOR[variant] : "secondary");
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium",
        COLOR_STYLES[resolvedColor],
        className
      )}
    >
      {dot ? <span className={cn("h-1.5 w-1.5 rounded-full", DOT_STYLES[resolvedColor])} /> : null}
      {children}
    </span>
  );
}